import type { JurisdictionCoverage } from '@mollusk/shared';
import { stateFipsToAbbr, stateName } from '@mollusk/shared';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import mapboxgl from 'mapbox-gl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../../api/client';
import { api } from '../../api/endpoints';
import { LoadingPill } from '../../components/LoadingPill';
import { CoverageLegend } from './CoverageLegend';
import { CoverageStatsHeadline } from './CoverageStatsHeadline';
import { JurisdictionDetailPanel } from './JurisdictionDetailPanel';
import {
  byFeatureId,
  coverageByStateFips,
  featuresBounds,
  heldJurisdictionFeatures,
  paintFeatures,
} from './geo';
import { EMPTY_FC, setSourceData } from './mapUtil';
import { countyLevelFeatures, topoToFeatures } from './topo';
import { summarizeUncovered } from './uncovered';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN ?? '';

const US_CENTER: [number, number] = [-98, 38];
const US_ZOOM = 3;

const STATES_FILL = 'permit-states-fill';
const STATES_LINE = 'permit-states-line';
const JURIS_FILL = 'permit-juris-fill';
const JURIS_LINE = 'permit-juris-line';

/**
 * US permit-coverage map (#171). States are shaded by the share of their
 * people we cover, and every jurisdiction we hold permits for is drawn as a
 * polygon (covered = amber, thin/partial = grey). It uses the same permits.json
 * as the headline numbers, so map, headline and Home all agree.
 */
export function PermitCoverageMap() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [ready, setReady] = useState(false);
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [activeJurisdiction, setActiveJurisdiction] = useState<JurisdictionCoverage | null>(null);
  const [showLegend, setShowLegend] = useState(true);

  const permits = useQuery({ queryKey: ['coverage-permits'], queryFn: api.coveragePermits });
  const stats = useQuery({ queryKey: ['coverage-stats'], queryFn: api.coverageStats });
  const statesGeo = useQuery({
    queryKey: ['coverage-geo', 'states'],
    queryFn: () => api.coverageGeo('states'),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const countiesGeo = useQuery({
    queryKey: ['coverage-geo', 'counties'],
    queryFn: () => api.coverageGeo('counties'),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const jurisGeo = useQuery({
    queryKey: ['coverage-geo', 'permit-jurisdictions'],
    queryFn: () => api.coverageGeo('permit-jurisdictions'),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const stateFeatures = useMemo(
    () => (statesGeo.data ? topoToFeatures(statesGeo.data, 'states') : []),
    [statesGeo.data],
  );
  const countyIndex = useMemo(() => {
    const m = new Map<string, GeoJSON.Feature>();
    for (const f of countiesGeo.data ? countyLevelFeatures(countiesGeo.data) : []) {
      if (f.id != null) m.set(String(f.id), f);
    }
    return m;
  }, [countiesGeo.data]);
  const placeIndex = useMemo(() => {
    const m = new Map<string, GeoJSON.Feature>();
    const fc = jurisGeo.data as GeoJSON.FeatureCollection | undefined;
    for (const f of fc?.features ?? []) {
      const g = f.properties?.place_geoid;
      if (typeof g === 'string') m.set(g, f);
    }
    return m;
  }, [jurisGeo.data]);

  const jurisdictionByGeoid = useMemo(() => {
    const m = new Map<string, JurisdictionCoverage>();
    for (const j of permits.data?.jurisdictions ?? []) m.set(j.place_geoid, j);
    return m;
  }, [permits.data]);

  const statesFC = useMemo(() => {
    if (stateFeatures.length === 0 || !permits.data) return null;
    return paintFeatures(stateFeatures, byFeatureId, coverageByStateFips(permits.data.states));
  }, [stateFeatures, permits.data]);
  const jurisFC = useMemo(
    () => heldJurisdictionFeatures(permits.data?.jurisdictions ?? [], placeIndex, countyIndex),
    [permits.data, placeIndex, countyIndex],
  );

  const selectedStateRow = useMemo(() => {
    if (!selectedState || !permits.data) return null;
    const abbr = stateFipsToAbbr(selectedState);
    const row = permits.data.states.find((s) => s.state === abbr);
    return row ? { name: stateName(selectedState) ?? row.state, row } : null;
  }, [selectedState, permits.data]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: US_CENTER,
      zoom: US_ZOOM,
    });
    // Scrolling the page shouldn't zoom the map; enable it after a click (#171).
    map.scrollZoom.disable();
    map.on('click', () => map.scrollZoom.enable());
    mapRef.current = map;
    map.on('load', () => {
      map.addSource(STATES_FILL, { type: 'geojson', data: EMPTY_FC });
      map.addSource(JURIS_FILL, { type: 'geojson', data: EMPTY_FC });
      map.addLayer({
        id: STATES_FILL,
        type: 'fill',
        source: STATES_FILL,
        paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.7 },
      });
      map.addLayer({
        id: STATES_LINE,
        type: 'line',
        source: STATES_FILL,
        paint: { 'line-color': '#ffffff', 'line-width': 1 },
      });
      map.addLayer({
        id: JURIS_FILL,
        type: 'fill',
        source: JURIS_FILL,
        paint: {
          'fill-color': ['case', ['get', 'covered'], '#f59e0b', '#94a3b8'],
          'fill-opacity': ['case', ['get', 'covered'], 0.6, 0.4],
        },
      });
      map.addLayer({
        id: JURIS_LINE,
        type: 'line',
        source: JURIS_FILL,
        paint: {
          'line-color': ['case', ['get', 'covered'], '#b45309', '#64748b'],
          'line-width': 0.8,
        },
      });

      map.on('click', STATES_FILL, (e) => {
        const id = e.features?.[0]?.id;
        if (id != null) setSelectedState(String(id));
      });
      map.on('click', JURIS_FILL, (e) => {
        const geoid = e.features?.[0]?.properties?.place_geoid;
        if (typeof geoid === 'string') {
          const j = jurisLookupRef.current.get(geoid);
          if (j) setActiveJurisdiction(j);
        }
      });
      for (const layer of [STATES_FILL, JURIS_FILL]) {
        map.on('mouseenter', layer, () => {
          map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mouseleave', layer, () => {
          map.getCanvas().style.cursor = '';
        });
      }
      setReady(true);
    });
    return () => {
      map.remove();
      mapRef.current = null;
      setReady(false);
    };
  }, []);

  // Click handler closes over a stable ref so it always sees the latest lookup.
  const jurisLookupRef = useRef(jurisdictionByGeoid);
  useEffect(() => {
    jurisLookupRef.current = jurisdictionByGeoid;
  }, [jurisdictionByGeoid]);

  useEffect(() => {
    if (mapRef.current && statesFC) setSourceData(mapRef.current, STATES_FILL, statesFC);
  }, [statesFC]);
  useEffect(() => {
    if (mapRef.current) setSourceData(mapRef.current, JURIS_FILL, jurisFC);
  }, [jurisFC]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (selectedState) {
      const bounds = featuresBounds(stateFeatures.filter((f) => String(f.id) === selectedState));
      if (bounds) map.fitBounds(bounds, { padding: 40, duration: 600 });
    } else {
      map.flyTo({ center: US_CENTER, zoom: US_ZOOM, duration: 600 });
    }
  }, [selectedState, stateFeatures, ready]);

  const error = permits.error ?? statesGeo.error ?? jurisGeo.error ?? countiesGeo.error;
  const loading =
    permits.isFetching || statesGeo.isFetching || jurisGeo.isFetching || countiesGeo.isFetching;

  return (
    <Box>
      {error instanceof ApiError ? <Typography color="error">{error.message}</Typography> : null}
      <CoverageStatsHeadline stats={stats.data} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 1,
          minHeight: 40,
          flexWrap: 'wrap',
        }}
      >
        {selectedState ? (
          <>
            <Button variant="outlined" size="small" onClick={() => setSelectedState(null)}>
              ← All states
            </Button>
            {selectedStateRow ? (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {selectedStateRow.name}: {(selectedStateRow.row.coverage * 100).toFixed(0)}% of
                  people covered ({selectedStateRow.row.covered_population.toLocaleString()} of{' '}
                  {selectedStateRow.row.total_population.toLocaleString()}) ·{' '}
                  {selectedStateRow.row.covered_jurisdictions} jurisdictions ·{' '}
                  {selectedStateRow.row.permit_count.toLocaleString()} permits. Click a jurisdiction
                  for detail.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {summarizeUncovered(selectedStateRow.row.uncovered)}
                </Typography>
              </Box>
            ) : null}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            States are shaded by the share of their people we cover. Each jurisdiction we hold
            permits for is drawn on top. Click a state to zoom in.
          </Typography>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={<LayersOutlinedIcon />}
          onClick={() => setShowLegend((s) => !s)}
          aria-pressed={showLegend}
        >
          {showLegend ? 'Hide legend' : 'Show legend'}
        </Button>
      </Box>
      <Box sx={{ position: 'relative', width: '100%', height: { xs: 380, sm: 520 } }}>
        <Box
          ref={containerRef}
          sx={{ position: 'absolute', inset: 0, borderRadius: 1, overflow: 'hidden' }}
        />
        {showLegend ? <CoverageLegend unit="share of people" showMembership /> : null}
        {loading ? (
          <LoadingPill
            label="Loading coverage…"
            sx={{ position: 'absolute', top: 12, right: 12 }}
          />
        ) : null}
      </Box>
      <JurisdictionDetailPanel
        jurisdiction={activeJurisdiction}
        onClose={() => setActiveJurisdiction(null)}
      />
    </Box>
  );
}
